import React, { useState } from 'react'
import { API_HOST, authenticatedFetchData } from '../../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Dialog } from '../../../common/Dialog'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  userId: string
  appContext: string
  onClose: () => void
}

export const CreateLoginLinkDialog = ({ userId, appContext, onClose }: Props) => {
  const [status, setStatus] = useState<RequestStatusType | 'password-enabled'>('idle')
  const [loginLink, setLoginLink] = useState('')
  const [copied, setCopied] = useState<boolean>(false)

  const createLoginLink = async () => {
    try {
      setStatus('pending')
      const signInLink = await authenticatedFetchData(
        `${API_HOST}/mytomra/v1.0/hotlinetool/create-signin-link?userId=${encodeURIComponent(userId)}`,
        {
          method: 'POST'
        }
      ).run()
      setLoginLink(signInLink)
      setStatus('success')
    } catch (error: any) {
      if (error.status === 412) {
        setStatus('password-enabled')
      } else {
        setStatus('failure')
        logError(new Error('Failed to create login link'), error)
      }
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(loginLink)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
      })
      .catch(() => {})
  }

  return (
    <>
      <Dialog ariaLabel={`Changes ${status}`} onClose={onClose}>
        <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 flex flex-col gap-y-6">
          <h1 className="text-xl">Create login link</h1>

          {status === 'idle' ? (
            <p>Create a login link for the user to sign in to {appContext}.</p>
          ) : status === 'success' ? (
            <div className="space-y-6">
              <p>Great! You can now forward the following link to the user:</p>

              <p className="p-4 border-2 rounded-lg flex items-center space-x-6 bg-gray-100">
                <b className="max-w-300 break-words text-emerald-700">{loginLink}</b>

                <Button disabled={copied} onClick={copyToClipboard}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </p>
            </div>
          ) : status === 'failure' ? (
            <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
              Whoopsie! Something went wrong.
            </div>
          ) : status === 'password-enabled' ? (
            <div className="rounded-lg border-l-[3px] border-amber-400 bg-white p-4 text-left shadow-md dark:bg-gray-900">
              The user has a password login enabled. Please ask the user to sign in with their password or generate a
              new one.
            </div>
          ) : null}

          {status !== 'success' && status !== 'password-enabled' && (
            <Button variant="primary" className="self-end" onClick={createLoginLink}>
              {status === 'pending' ? <div className="loadingSpinner" /> : 'Create link'}
            </Button>
          )}
        </div>
      </Dialog>
    </>
  )
}
